import React, { useState, useEffect } from 'react'
import { X, ThumbsUp, ThumbsDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { FEEDBACK_RATINGS } from '@shared/schemas/feedback'
import { cn } from '@/lib/utils'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  messageId: string
  currentFeedback?: {
    rating: number
    comment: string | null
  } | null
  onSubmit: (rating: number, comment?: string) => Promise<void>
  submitting?: boolean
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  messageId,
  currentFeedback,
  onSubmit,
  submitting = false
}) => {
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [charCount, setCharCount] = useState(0)
  
  const maxChars = 1000

  // Initialize form state when modal opens or currentFeedback changes
  useEffect(() => {
    if (isOpen) {
      setSelectedRating(currentFeedback?.rating || null)
      setComment(currentFeedback?.comment || '')
      setCharCount(currentFeedback?.comment?.length || 0)
    }
  }, [isOpen, currentFeedback])

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length <= maxChars) {
      setComment(value)
      setCharCount(value.length)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (selectedRating === null) return
    
    try {
      await onSubmit(selectedRating, comment.trim() || undefined)
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleClose = () => {
    if (!submitting) {
      onClose()
    }
  }

  const isSubmitDisabled = selectedRating === null || submitting
  const hasChanges = 
    selectedRating !== (currentFeedback?.rating || null) ||
    comment.trim() !== (currentFeedback?.comment || '').trim()

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Feedback
            {selectedRating === FEEDBACK_RATINGS.THUMBS_UP && (
              <ThumbsUp className="w-4 h-4 text-green-600" />
            )}
            {selectedRating === FEEDBACK_RATINGS.THUMBS_DOWN && (
              <ThumbsDown className="w-4 h-4 text-red-600" />
            )}
          </DialogTitle>
          <DialogDescription>
            Help us improve by sharing your feedback on this response.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">How would you rate this response?</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={selectedRating === FEEDBACK_RATINGS.THUMBS_UP ? 'default' : 'outline'}
                size="sm"
                className={cn(
                  'flex items-center gap-2',
                  selectedRating === FEEDBACK_RATINGS.THUMBS_UP
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'hover:bg-green-50 hover:text-green-600 hover:border-green-300'
                )}
                onClick={() => setSelectedRating(FEEDBACK_RATINGS.THUMBS_UP)}
                disabled={submitting}
              >
                <ThumbsUp className="w-4 h-4" />
                Helpful
              </Button>
              <Button
                type="button"
                variant={selectedRating === FEEDBACK_RATINGS.THUMBS_DOWN ? 'default' : 'outline'}
                size="sm"
                className={cn(
                  'flex items-center gap-2',
                  selectedRating === FEEDBACK_RATINGS.THUMBS_DOWN
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'hover:bg-red-50 hover:text-red-600 hover:border-red-300'
                )}
                onClick={() => setSelectedRating(FEEDBACK_RATINGS.THUMBS_DOWN)}
                disabled={submitting}
              >
                <ThumbsDown className="w-4 h-4" />
                Not helpful
              </Button>
            </div>
          </div>

          {/* Comment Section */}
          <div className="space-y-2">
            <Label htmlFor="feedback-comment" className="text-sm font-medium">
              Additional comments (optional)
            </Label>
            <div className="relative">
              <Textarea
                id="feedback-comment"
                placeholder="Tell us what worked well or what could be improved..."
                value={comment}
                onChange={handleCommentChange}
                disabled={submitting}
                rows={3}
                className="resize-none"
              />
              <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                {charCount}/{maxChars}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitDisabled}
              className="min-w-20"
            >
              {submitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </div>
              ) : hasChanges ? (
                'Save Feedback'
              ) : (
                'No Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default FeedbackModal